import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { AuthPolicies } from '../../../auth/auth-policies';
import { RepairCodeExclusionAdvancedFilterComponent } from '../repair-code-exclusion-advanced-filter/repair-code-exclusion-advanced-filter.component';
import { CommonModule } from '@angular/common';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import { ToastrService } from '../../../services/toastr-service';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { RepairCodeExclusionFilterForm } from '../../../form-model/repair-code-exclusion-filter.form.model';
import { RepairCodeExclusionSearchTextForm } from '../../../form-model/repair-code-exclusion-search-text.form.model';
import { environment } from '../../../../environments/environment';
import { RepairCodeExclusionService } from '../../../services/repair-code-exclusion.service';
import { getFilterValue as getSharedFilterValue, removeFilter as removeSharedFilter } from '../../../helper/filter-helpers';
import { DownloadFilterCriteria } from '../../../models/download-filter.model';
import {
  buildDownloadFilters,
  clearTaggedFilterForm,
  downloadFilteredFile,
  ensureTaggedFilterControls,
  FilterTagDefinition,
  removeTaggedValue,
  restoreTaggedFilterForm,
  restoreTaggedFilterSelections,
  subscribeToTaggedFilterChanges,
  TaggedFilter,
  TaggedFilterValue,
  updateTaggedFilters,
} from '../../../helper/tagged-filter-helpers';

const FILTER_TAGS: FilterTagDefinition[] = [
  { key: 'Repair Code - Description', control: 'RepairCodeDescriptionList', downloadColumn: 'repair_code' },
  { key: 'Mode', control: 'RepairModeList', downloadColumn: 'repair_mode' },
];

const DOWNLOAD_COLUMNS = ['repair_mode', 'repair_code', 'description', 'excluded_repair_code'];

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

@Component({
  selector: 'app-repair-code-exclusion-filters',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    RepairCodeExclusionAdvancedFilterComponent,
    TagWithOverflowComponent,
  ],
  templateUrl: './repair-code-exclusion-filters.component.html',
  styleUrl: './repair-code-exclusion-filters.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairCodeExclusionFiltersComponent implements AfterViewInit {
  showAdvanceFilters = false;
  isDownload = false;
  canAddNew = this.authPolicies.canAddRepairCodeExclusion && environment.Features.EnableRepairCodeExclusion;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    filterForm: FormGroup<RepairCodeExclusionFilterForm>;
    searchTextForm: FormGroup<RepairCodeExclusionSearchTextForm>;
  }>;
  taggedFilters: TaggedFilter[] = [];

  @ViewChild(RepairCodeExclusionAdvancedFilterComponent)
  advanceFilterComponent!: RepairCodeExclusionAdvancedFilterComponent;

  constructor(
    public authPolicies: AuthPolicies,
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly service: RepairCodeExclusionService,
  ) {}

  ngAfterViewInit(): void {
    this.ensureFlagControlsExist();
    this.subscribeToFormChanges();
    this.setFilterFormValue();
  }

  private ensureFlagControlsExist(): void {
    ensureTaggedFilterControls(
      this.formBuilder,
      this.filterFormGroup.controls.filterForm,
      this.filterFormGroup.controls.searchTextForm,
      FILTER_TAGS,
      'applyFilter',
      'appliedSearchTextFilter',
    );
  }

  private setFilterFormValue(): void {
    restoreTaggedFilterForm(this.filterFormGroup.controls.filterForm, this.service.getFilterFormValue(), 'applyFilter', () =>
      this.updateSelectedFiltersSection(),
    );
  }

  private subscribeToFormChanges(): void {
    subscribeToTaggedFilterChanges(
      this.filterFormGroup.controls.filterForm,
      this.filterFormGroup.controls.searchTextForm,
      'applyFilter',
      'appliedSearchTextFilter',
      () => this.persistAppliedFilters(),
      () => this.updateSelectedFiltersSection(),
    );
  }

  private persistAppliedFilters(): void {
    this.filterFormGroup.controls.filterForm.get('applyFilter')!.setValue(null, { emitEvent: false });
    this.showAdvanceFilters = false;
    this.updateSelectedFiltersSection();
    this.service.setFilterFormValue(this.filterFormGroup.controls.filterForm.getRawValue());
  }

  unselectRepairCode(): (_: string) => void {
    return (code) => this.unselectTagValue(FILTER_TAGS[0], code);
  }

  unselectMode(): (_: string) => void {
    return (mode) => this.unselectTagValue(FILTER_TAGS[1], mode);
  }

  private unselectTagValue(definition: FilterTagDefinition, value: string): void {
    const updatedValues = removeTaggedValue(this.taggedFilters, definition.key, value);
    this.filterFormGroup.controls.filterForm.get(definition.control)?.setValue(updatedValues.length ? updatedValues : null);
    this.filterFormGroup.controls.filterForm.get('applyFilter')?.setValue(true);
    this.updateSelectedFiltersSection();
  }

  showAdvanceFiltersModal(): void {
    this.showAdvanceFilters = true;
  }

  onDownload(): void {
    this.isDownload = true;
    downloadFilteredFile(
      this.service.downloadToExcel({ request: { columns: DOWNLOAD_COLUMNS, filters: this.buildDownloadFilters() } }),
      'repair-code-grade-mapping',
      this.toastrService,
      () => (this.isDownload = false),
    );
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    return buildDownloadFilters(this.filterFormGroup.controls.filterForm, FILTER_TAGS);
  }

  closeAdvanceFiltersModal(): void {
    const wasOpen = restoreTaggedFilterSelections(
      this.filterFormGroup.controls.filterForm,
      this.showAdvanceFilters,
      this.service.getFilterFormValue(),
      FILTER_TAGS,
    );
    if (wasOpen) this.showAdvanceFilters = false;
    this.updateSelectedFiltersSection();
  }

  clearAllSelectedFilters(): void {
    clearTaggedFilterForm(
      this.filterFormGroup,
      this.filterFormGroup.controls.filterForm,
      'applyFilter',
      () => this.service.setFilterFormValue(null),
      () => this.updateSelectedFiltersSection(),
    );
  }

  updateSelectedFiltersSection(): void {
    updateTaggedFilters(this.taggedFilters, this.filterFormGroup.controls.filterForm, FILTER_TAGS);
  }

  markAsTagged(key: string, newValue: TaggedFilterValue | null): void {
    const currentValue = this.getFilterValue(key);
    if (currentValue !== newValue) {
      this.removeFilter(key);
      if (newValue !== null && newValue !== '' && (!Array.isArray(newValue) || newValue.length > 0)) {
        this.taggedFilters.unshift({ key, value: newValue });
      }
    }
  }

  removeFilter(key: string): void {
    removeSharedFilter(this.taggedFilters, key);
  }

  getFilterValue(key: string): TaggedFilterValue | null {
    return getSharedFilterValue(this.taggedFilters, key) ?? null;
  }

  getTaggedRepairCodes(): string[] {
    const filter = this.taggedFilters.find((f) => f.key === 'Repair Code - Description');
    return filter && isStringArray(filter.value) ? filter.value : [];
  }

  getTaggedModes(): string[] {
    const filter = this.taggedFilters.find((f) => f.key === 'Mode');
    return filter && isStringArray(filter.value) ? filter.value : [];
  }

  isArrayType(value: TaggedFilterValue): boolean {
    return Array.isArray(value);
  }

  onAddNewClicked(): void {
    this.router.navigate(['/master-data/repair-code-exclusion/add-new']);
  }
}
